import genAI, { getSafetySettings, handleGeminiError } from '../lib/geminiClient';
import { supabase } from '../lib/supabase';

/**
 * Gemini Chat Service
 * Provides AI chatbot functionality with role-based context
 */

/**
 * Role-specific system prompts for contextual responses
 */
const ROLE_PROMPTS = {
  student: `You are a helpful AI assistant for students in the Smart Cities Challenge Program. 
You help students with:
- Understanding their team tasks and deliverables
- Learning about smart cities concepts
- Finding resources and learning materials
- Collaborating with team members
- Managing their project timeline
Keep responses concise, encouraging, and educational.`,

  pcf_admin: `You are a helpful AI assistant for PCF Administrators managing the Smart Cities Challenge Program.
You help administrators with:
- Managing teams and monitoring their progress
- Understanding platform analytics and metrics
- Supporting users across the platform
- Managing content and announcements
- Overseeing program operations
Keep responses professional, data-driven, and actionable.`,

  city_official: `You are a helpful AI assistant for City Officials participating in the Smart Cities Challenge Program.
You help city officials with:
- Understanding problem statements and challenges
- Monitoring team progress on city-specific projects
- Reviewing deliverables and providing feedback
- Collaborating with teams and mentors
- Managing meeting schedules
Keep responses professional, solution-oriented, and supportive.`
};

/**
 * Generate chatbot response with role context
 * @param {string} message - User message
 * @param {string} role - User role (student, pcf_admin, city_official)
 * @param {string} userId - Optional user ID for personalization
 * @param {Array} history - Chat history for context
 * @returns {Promise<{response: string, updatedHistory: Array}>}
 */
export const generateChatResponse = async (message, role = 'student', userId = null, history = []) => {
  try {
    const model = genAI?.getGenerativeModel({ 
      model: 'gemini-2.5-flash',
      safetySettings: getSafetySettings()
    });

    // Get role-specific system prompt
    const systemPrompt = ROLE_PROMPTS?.[role] || ROLE_PROMPTS?.student;

    // Build chat history with system prompt
    const chatHistory = [
      { role: 'user', parts: [{ text: systemPrompt }] },
      { role: 'model', parts: [{ text: 'I understand my role and I\'m ready to help you.' }] },
      ...history
    ];

    const chat = model?.startChat({ history: chatHistory });

    // Add user context if userId provided
    const contextualMessage = userId 
      ? `[User ID: ${userId}] ${message}` 
      : message;

    const result = await chat?.sendMessage(contextualMessage);
    const response = await result?.response;
    const text = response?.text();

    const updatedHistory = [
      ...history,
      { role: 'user', parts: [{ text: message }] },
      { role: 'model', parts: [{ text }] }
    ];

    return { response: text, updatedHistory };
  } catch (error) {
    const errorMessage = handleGeminiError(error);
    throw new Error(errorMessage);
  }
};

/**
 * Generate streaming chatbot response
 * @param {string} message - User message
 * @param {string} role - User role
 * @param {Function} onChunk - Callback for each text chunk
 * @param {Array} history - Chat history
 * @returns {Promise<Array>} Updated chat history
 */
export const generateStreamingChatResponse = async (message, role = 'student', onChunk, history = []) => {
  try {
    const model = genAI?.getGenerativeModel({ 
      model: 'gemini-2.5-flash',
      safetySettings: getSafetySettings()
    });

    const systemPrompt = ROLE_PROMPTS?.[role] || ROLE_PROMPTS?.student;

    const chatHistory = [
      { role: 'user', parts: [{ text: systemPrompt }] },
      { role: 'model', parts: [{ text: 'I understand my role and I\'m ready to help you.' }] },
      ...history
    ];

    const chat = model?.startChat({ history: chatHistory });

    const result = await chat?.sendMessageStream(message);
    let fullResponse = '';

    for await (const chunk of result?.stream) {
      const text = chunk?.text();
      if (text) {
        fullResponse += text;
        onChunk(text);
      }
    }

    const updatedHistory = [
      ...history,
      { role: 'user', parts: [{ text: message }] },
      { role: 'model', parts: [{ text: fullResponse }] }
    ];

    return updatedHistory;
  } catch (error) {
    const errorMessage = handleGeminiError(error);
    throw new Error(errorMessage);
  }
};

/**
 * Generate RAG-based chatbot response with document context
 * @param {string} message - User message
 * @param {string} role - User role
 * @param {Function} onChunk - Callback for streaming chunks
 * @param {Array} history - Chat history
 * @returns {Promise<void>}
 */
export const generateRAGChatResponse = async (message, role = 'student', onChunk, history = []) => {
  try {
    // Step 1: Generate embedding for user query
    const model = genAI?.getGenerativeModel({ 
      model: 'text-embedding-004'
    });
    
    const embeddingResult = await model?.embedContent(message);
    const queryEmbedding = embeddingResult?.embedding?.values;

    if (!queryEmbedding) {
      throw new Error('Failed to generate query embedding');
    }

    // Step 2: Search for relevant document chunks using vector similarity
    const { data: relevantChunks, error: searchError } = await supabase?.rpc('match_doc_chunks', {
        query_embedding: queryEmbedding,
        match_threshold: 0.5,
        match_count: 5
      });

    if (searchError) {
      console.error('Error searching documents:', searchError);
      throw new Error('Failed to search knowledge base');
    }

    // Step 3: Build context from relevant chunks
    let contextText = '';
    if (relevantChunks?.length > 0) {
      contextText = relevantChunks
        ?.map((chunk, index) => `[Context ${index + 1}]: ${chunk?.content}`)
        ?.join('\n\n');
    }

    // Step 4: Build enhanced system prompt with context
    const systemPrompt = ROLE_PROMPTS?.[role] || ROLE_PROMPTS?.student;
    const enhancedPrompt = contextText 
      ? `${systemPrompt}\n\nYou have access to the following information from uploaded documents:\n\n${contextText}\n\nUse this information to provide accurate answers. If the question cannot be answered from the provided context, let the user know.`
      : `${systemPrompt}\n\nNote: No relevant documents found in the knowledge base for this query. Provide a helpful general response based on your training.`;

    // Step 5: Generate streaming response with context
    const chatModel = genAI?.getGenerativeModel({ 
      model: 'gemini-2.5-flash',
      safetySettings: getSafetySettings()
    });

    const chatHistory = [
      { role: 'user', parts: [{ text: enhancedPrompt }] },
      { role: 'model', parts: [{ text: 'I understand and I\'m ready to help based on the available knowledge.' }] },
      ...history
    ];

    const chat = chatModel?.startChat({ history: chatHistory });
    const result = await chat?.sendMessageStream(message);

    // Step 6: Stream response chunks
    for await (const chunk of result?.stream) {
      const text = chunk?.text();
      if (text) {
        onChunk(text);
      }
    }

  } catch (error) {
    console.error('RAG chat error:', error);
    const errorMessage = handleGeminiError(error);
    throw new Error(errorMessage);
  }
};

/**
 * Check if documents exist in knowledge base
 * @returns {Promise<boolean>}
 */
export const hasDocuments = async () => {
  try {
    const { count, error } = await supabase?.from('program_documents')?.select('*', { count: 'exact', head: true });

    if (error) {
      console.error('Error checking documents:', error);
      return false;
    }

    return count > 0;
  } catch (error) {
    console.error('Error checking documents:', error);
    return false;
  }
};

export default {
  generateChatResponse,
  generateStreamingChatResponse,
  generateRAGChatResponse,
  hasDocuments
};